import React from "react";
import type { Collection } from "@atlas/shared";

const CSS = `
.atlas-coll{
  position:relative; display:flex; gap:14px; align-items:center;
  background:var(--surface-card); border:1px solid var(--border-subtle);
  border-radius:var(--radius-lg); box-shadow:var(--shadow-sm), var(--ring-top);
  padding:14px; cursor:pointer; transition:var(--t-colors), transform var(--dur-quick) var(--ease-out);
  text-align:left; width:100%; font-family:var(--font-sans);
}
.atlas-coll:hover{ transform:translateY(-2px); border-color:var(--border-default); box-shadow:var(--shadow-md), var(--ring-top); }
.atlas-coll--done{ border-color:var(--border-gold); box-shadow:var(--glow-gold-sm), var(--ring-top); }
.atlas-coll__media{
  flex:0 0 auto; width:60px; height:60px; border-radius:var(--radius-md);
  display:flex; align-items:center; justify-content:center; overflow:hidden;
  background:linear-gradient(150deg, var(--surface-raised), var(--surface-sunken));
  border:1px solid var(--border-default); color:var(--gold-400);
}
.atlas-coll__media img{ width:100%; height:100%; object-fit:cover; }
.atlas-coll__body{ flex:1; min-width:0; display:flex; flex-direction:column; gap:8px; }
/* flex-wrap: on narrow cards the count drops below the title instead of
   painting over it (the left span crushes to 0 width otherwise). */
.atlas-coll__top{ display:flex; flex-wrap:wrap; align-items:baseline; justify-content:space-between; gap:2px 10px; }
.atlas-coll__top > span:first-child{ flex:1 1 auto; min-width:0; }
.atlas-coll__type{ display:block; font-family:var(--font-mono); font-size:10px; text-transform:uppercase; letter-spacing:.12em; color:var(--text-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.atlas-coll__title{ font-family:var(--font-display); font-weight:600; font-size:16px; color:var(--text-primary); letter-spacing:-.01em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.atlas-coll__count{ font-family:var(--font-mono); font-size:13px; font-weight:700; color:var(--text-primary); font-variant-numeric:tabular-nums; flex:0 0 auto; white-space:nowrap; }
.atlas-coll__count b{ color:var(--gold-400); }
.atlas-coll__track{ height:7px; border-radius:var(--radius-pill); background:var(--surface-sunken); overflow:hidden; box-shadow:inset 0 1px 2px rgba(0,0,0,.4); }
.atlas-coll__fill{ height:100%; border-radius:var(--radius-pill); background:linear-gradient(90deg, var(--gold-600), var(--gold-400)); transition:width var(--dur-slow) var(--ease-out); }
.atlas-coll--done .atlas-coll__fill{ background:linear-gradient(90deg, var(--gold-500), var(--gold-300)); box-shadow:0 0 10px rgba(244,183,64,.5); }
.atlas-coll__pct{ font-family:var(--font-mono); font-size:11px; color:var(--text-muted); }

/* Full-bleed hero variant — used when a cover image is supplied. Cards
   without an image keep the default thumbnail layout above untouched. */
.atlas-coll--hero{
  flex-direction:column; align-items:stretch; justify-content:flex-end;
  min-height:168px; padding:16px; gap:0; background-size:cover; background-position:center;
  background-origin:border-box; background-color:var(--surface-sunken);
}
.atlas-coll--hero:hover{ transform:translateY(-2px); }
.atlas-coll--hero .atlas-coll__media{ display:none; }
.atlas-coll--hero .atlas-coll__body{ gap:8px; position:relative; z-index:1; justify-content:flex-end; }
.atlas-coll--hero .atlas-coll__type{ color:rgba(255,255,255,.78); }
.atlas-coll--hero .atlas-coll__title{ color:#fff; text-shadow:0 1px 3px rgba(0,0,0,.5); }
.atlas-coll--hero .atlas-coll__count{ color:#fff; text-shadow:0 1px 3px rgba(0,0,0,.5); }
.atlas-coll--hero .atlas-coll__count b{ color:var(--gold-300); }
.atlas-coll--hero .atlas-coll__track{ background:rgba(255,255,255,.2); box-shadow:inset 0 1px 2px rgba(0,0,0,.5); }
.atlas-coll--hero .atlas-coll__pct{ color:rgba(255,255,255,.78); }
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-coll-css")) {
    const s = document.createElement("style");
    s.id = "atlas-coll-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

/** Walked / total km for a collection that is exactly one trail. */
export interface TrailDistance {
  walkedKm: number;
  totalKm: number;
}

/**
 * "0 / 1" says nothing for a single-trail collection, so those cards read as
 * distance. walkedKm is distance-weighted by the server; an API without it
 * falls back to length x pct (exact wherever sections are equal length,
 * nearly every trail). Mirrors mobile CollectionCard countsLabel.
 */
export function trailDistanceOf(c: Collection): TrailDistance | null {
  if (!c.trailSlug || c.distanceKm == null || c.distanceKm <= 0) return null;
  const walked = c.walkedKm ?? (c.distanceKm * Math.min(100, Math.max(0, c.pct))) / 100;
  return { walkedKm: walked, totalKm: c.distanceKm };
}

function formatKm(km: number): string {
  return km > 0 && km < 10 ? km.toFixed(1) : String(Math.round(km));
}

interface CollectionCardProps {
  title: string;
  type?: string;
  value?: number;
  max?: number;
  icon?: React.ReactNode;
  image?: string | null;
  className?: string;
  onClick?: () => void;
  /** Fractional progress % (partial trail/region credit). Falls back to
   *  value/max when absent. "Completed" still requires value >= max. */
  pct?: number;
  /** Single-trail collections: show km instead of the item count. */
  distance?: TrailDistance | null;
  /** ATL-168: companion lists beside this one ("+ 52 tops"). */
  note?: string | null;
}

export function CollectionCard({
  title,
  type = "Collection",
  value = 0,
  max = 100,
  icon = null,
  image = null,
  className = "",
  onClick,
  pct: pctProp,
  note = null,
  distance = null,
}: CollectionCardProps) {
  ensure();
  const pct = Math.max(0, Math.min(100, pctProp ?? (value / max) * 100));
  const done = max > 0 && value >= max;
  return (
    <button
      className={[
        "atlas-coll",
        image ? "atlas-coll--hero" : "",
        done ? "atlas-coll--done" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        image
          ? {
              backgroundImage: `linear-gradient(180deg, rgba(9,12,16,0.25) 0%, rgba(9,12,16,0.85) 100%), url("${image}")`,
            }
          : undefined
      }
      onClick={onClick}
    >
      {!image && <span className="atlas-coll__media">{icon}</span>}
      <span className="atlas-coll__body">
        <span className="atlas-coll__top">
          <span style={{ minWidth: 0 }}>
            <span className="atlas-coll__type">{type}</span>
            <span className="atlas-coll__title" style={{ display: "block" }}>
              {title}
            </span>
          </span>
          <span className="atlas-coll__count">
            {distance ? (
              <>
                <b>{formatKm(distance.walkedKm)} km</b> / {formatKm(distance.totalKm)} km
              </>
            ) : (
              <>
                <b>{value}</b> / {max}
              </>
            )}
          </span>
        </span>
        <span className="atlas-coll__track">
          <span
            className="atlas-coll__fill"
            style={{ width: `${pct}%`, display: "block" }}
          />
        </span>
        <span className="atlas-coll__pct">
          {done
            ? "Completed"
            : distance
              ? `${Math.round(pct)}% complete · ${formatKm(Math.max(0, distance.totalKm - distance.walkedKm))} km to go`
              : `${Math.round(pct)}% complete · ${max - value} remaining`}
          {note ? ` · ${note}` : ""}
        </span>
      </span>
    </button>
  );
}
